import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';

const ResetPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, loading: authLoading, updatePassword, verifyRecoveryToken } = useAuth();
  const [searchParams] = useSearchParams();
  const tokenHash = searchParams.get('token_hash');
  const recoveryVerifiedRef = useRef(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // A token_hash in the query is not a session yet. Outlook Safe Links only
  // GETs the page, so the token stays valid until the form is submitted.
  useEffect(() => {
    if (tokenHash) return;
    if (!authLoading && !user) {
      navigate('/authentication', { replace: true });
    }
  }, [tokenHash, user, authLoading, navigate]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    if (!password?.trim()) {
      setError(t('auth.passwordRequired'));
      return;
    }
    if (password.length < 8) {
      setError(t('auth.passwordMinLength', { count: 8 }));
      return;
    }
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/.test(password)) {
      setError(t('auth.passwordComplexity'));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('auth.passwordsDoNotMatch'));
      return;
    }
    setLoading(true);
    if (tokenHash && !recoveryVerifiedRef.current) {
      const { error: verifyError } = await verifyRecoveryToken(tokenHash);
      if (verifyError) {
        setLoading(false);
        setError(t('auth.resetLinkExpired'));
        return;
      }
      recoveryVerifiedRef.current = true;
    }
    const { error: err } = await updatePassword(password);
    setLoading(false);
    if (err) {
      setError(err?.message || t('auth.updatePasswordFailed'));
      return;
    }
    setSuccess(true);
    setTimeout(() => navigate('/dashboard', { replace: true }), 2000);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (!user && !tokenHash) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>{t('auth.resetPageTitle')}</title>
        <meta name="description" content={t('auth.resetPageDescription')} />
      </Helmet>
      <div className="min-h-screen bg-background flex items-center justify-center p-4 md:p-6 lg:p-8">
        <div className="w-full max-w-md">
          <div className="bg-card rounded-xl md:rounded-2xl shadow-elevation-lg border border-border p-6 md:p-8">
            <h1 className="text-xl md:text-2xl font-semibold text-foreground mb-2">
              {t('auth.resetTitle')}
            </h1>
            <p className="text-sm text-muted-foreground mb-6">
              {t('auth.resetSubtitle')}
            </p>

            {success ? (
              <div className="p-4 bg-success/10 border border-success/20 rounded-lg">
                <p className="text-sm text-foreground">{t('auth.passwordUpdated')}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  label={t('auth.newPassword')}
                  type="password"
                  placeholder={t('auth.newPasswordPlaceholder')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  description={t('auth.passwordRequirements')}
                  descriptionClassName="text-[11px] leading-snug"
                  error={error}
                  disabled={loading}
                  autoFocus
                />
                <Input
                  label={t('auth.confirmNewPassword')}
                  type="password"
                  placeholder={t('auth.confirmNewPasswordPlaceholder')}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading}
                />
                <Button type="submit" fullWidth loading={loading}>
                  {t('auth.updatePassword')}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ResetPassword;
